import type { Typeface } from '../core/types';

/**
 * Font files for each type style, loaded only when that style is used (or previewed).
 * The default (Instrument) and Editorial reuse the fonts bundled in main.ts; System downloads
 * nothing. Vite turns each import into its own CSS chunk, served from this origin (CSP-safe).
 */
const loaders: Partial<Record<Typeface, () => Promise<unknown>>> = {
  hyperlegible: () =>
    Promise.all([
      import('@fontsource/atkinson-hyperlegible/400.css'),
      import('@fontsource/atkinson-hyperlegible/700.css'),
    ]),
  grotesk: () => import('@fontsource-variable/space-grotesk/wght.css'),
  lexend: () => import('@fontsource-variable/lexend/wght.css'),
};

const loaded = new Map<Typeface, Promise<unknown>>();

export function loadTypeface(t: Typeface): Promise<unknown> {
  const load = loaders[t];
  if (!load) return Promise.resolve();
  let p = loaded.get(t);
  if (!p) {
    p = load().catch(() => loaded.delete(t)); // offline first visit: retry next time
    loaded.set(t, p);
  }
  return p;
}

/** Applies a type style; the page keeps its current font until the new one has loaded. */
export function applyTypeface(t: Typeface) {
  void loadTypeface(t).then(() => {
    document.documentElement.dataset.type = t;
  });
}

/** Applies the text size (percent) — rem-based text everywhere follows it. */
export function applyTextScale(percent: number) {
  document.documentElement.style.setProperty('--text-scale', String(percent / 100));
  // breakpoints are in px, so very large text gets its own compact rules
  document.documentElement.dataset.textsize = percent >= 140 ? 'xl' : percent >= 120 ? 'lg' : 'md';
}

export const preloadAllTypefaces = () =>
  Promise.all(Object.keys(loaders).map((t) => loadTypeface(t as Typeface)));
